import { twMerge } from 'tailwind-merge';
import {
  normalizeWbsLevel,
  WBS_INDENT_STEP_PX,
} from './utils/epcScheduleWbsLevelStyle';

type WbsTaskTreeGutterProps = {
  /** Raw or normalized outline level */
  level: number;
  /** Timeline rows are short — cap guides so labels don’t overflow (0 = no cap). */
  maxDepth?: number;
  className?: string;
};

/**
 * Vertical guide “rails” for WBS depth: each level under the project root gets a column;
 * the active branch uses a brand-green accent on the last rail (common tree / outline UI pattern).
 */
export function WbsTaskTreeGutter({ level, maxDepth = 0, className }: WbsTaskTreeGutterProps) {
  const L = normalizeWbsLevel(level);
  let depth = Math.max(0, L - 1);
  if (maxDepth > 0) {
    depth = Math.min(depth, maxDepth);
  }

  if (depth === 0) {
    return (
      <div
        className={twMerge('shrink-0', className)}
        style={{ width: 18 }}
        aria-hidden
      />
    );
  }

  return (
    <div
      className={twMerge('flex h-full min-h-[2.5rem] shrink-0 items-stretch', className)}
      aria-hidden
    >
      {Array.from({ length: depth }).map((_, i) => {
        const isLastRail = i === depth - 1;
        return (
          <div
            key={i}
            className="relative flex shrink-0 justify-center"
            style={{ width: WBS_INDENT_STEP_PX }}
          >
            <div
              className={twMerge(
                'absolute top-1 bottom-1 w-[2px] rounded-full',
                isLastRail ? 'bg-[rgba(51,146,116,0.75)]' : 'bg-[rgba(11,59,46,0.10)]'
              )}
              style={{ left: '50%', transform: 'translateX(-50%)' }}
            />
          </div>
        );
      })}
    </div>
  );
}
